import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import KeyPad from './components/Keypad';
import Contacts from './components/Contacts';
import Recents from './components/Recents';
import { Global, css } from '@emotion/react';
import JsSIP from 'jssip';
import Bnums from './components/Bnums';
import PersonIcon from '@mui/icons-material/Person';
import { CallEnd, Dialpad, Close, Pause, PlayArrow } from '@mui/icons-material';
import { SvgIcon, Skeleton } from '@mui/material';
import IncomingCalls from './components/IncomingCalls';
import CallWaiting from './components/CallWaiting';
import { API_CONFIG, WEBRTC_CONFIG } from './constants/config';
import { getBnums } from './services/webRTCService';
import {
    validatePhoneNumberWithLeadingZeroFallback,
    toE164,
} from '@/utils/phone-validation';

const GlobalStyles = css`
    @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Roboto:ital,wght@0,100..900;1,100..900&display=swap');
    body {
        overflow-x: hidden;
    }
    .fixBottom {
        padding-bottom: 115px;
    }
`;

const FooterPanel = styled.div`
    border-top: 1px solid #eaecf0;
    background-color: rgb(252 253 253/1);
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    gap: 1rem;
    height: 90px;
    .panel-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: end;
        gap: 1rem;
        cursor: pointer;
        padding-bottom: 8px;
        min-width: 56px;
        div {
            display: flex;
            svg {
                color: rgb(71 84 103/1);
                width: 24px;
                height: 24px;
            }
        }

        span {
            font-size: 10px;
            line-height: 12px;
            color: rgb(102 112 133/1);
        }
    }
    .panel-section.active-section {
        gap: 1px !important;
        div {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 56px;
            width: 56px;
            border-radius: 28px;
            border: 1px solid #f2f4f7;
            background-color: #f2f4f7;
        }
    }
`;

const HeaderPanel = styled.div`
    padding: 9px 14px;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: start;
    background-color: rgb(249 250 251 / 1);
    border-bottom: 1px solid rgb(234 236 240 / 1);
    h2 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        font-family: 'Inter', sans-serif;
        line-height: 1.5rem;
        color: #101828;
    }
    .bnum-info {
        font-family: 'Inter', sans-serif;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        color: rgb(102 112 133/1);
        font-size: 14px;
        line-height: 20px;
        font-weight: 500;
        .select-bnums {
            display: flex;
            flex-direction: row;
            gap: 0.5rem;
            cursor: pointer;
            span {
                max-width: 150px;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
            }
            svg {
                width: 1rem;
                height: 1rem;
                color: rgb(152 162 179 / 1);
            }
        }
    }
    .connection-status {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 6px;
        font-size: 12px;
        font-family: 'Inter', sans-serif;
        .status-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            &.connected {
                background-color: #10b981;
            }
            &.connecting {
                background-color: #f59e0b;
                animation: pulse 1.5s infinite;
            }
            &.disconnected {
                background-color: #ef4444;
            }
        }
        @keyframes pulse {
            0% {
                opacity: 1;
            }
            50% {
                opacity: 0.5;
            }
            100% {
                opacity: 1;
            }
        }
    }
`;

const CallDesign = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: #000;
    padding-top: 2rem;
    z-index: 100;
    span {
        font-family: 'Roboto', sans-serif;
        color: #fafafa;
    }
    .callTimer {
        font-size: 16px;
        line-height: 24px;
    }
    .callName {
        font-size: 24px;
        font-weight: 900;
        line-height: 22px;
        margin-top: 0.5rem;
    }
    .callNumber {
        font-size: 18px;
        font-weight: 400;
        line-height: 22px;
        margin-top: 1rem;
    }
    .callButton {
        border: none;
        width: 70px;
        height: 70px;
        border-radius: 50%;
        /* margin-top: 3rem; */
        svg {
            color: white;
            width: 24px;
            height: 24px;
        }
    }
`;

const InProgressCalling = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: #000;
    padding-top: 2rem;
    z-index: 100;
    div {
        margin-top: 230px;
        display: flex;
        flex-direction: column;
        gap: 2rem;
        img {
            width: 160px;
        }
        span {
            font-family: 'Roboto', sans-serif;
            color: #fafafa;
            font-size: 1.4rem;
        }
    }
`;

// Add this component inside your WebRTC.jsx file, before the WebRTC component
const DtmfKeypad = styled.div`
    /* position: absolute;
    bottom: 100px;
    left: 50%;
    transform: translateX(-50%); */
    background-color: rgba(0, 0, 0, 0.8);
    border-radius: 12px;
    padding: 15px;
    width: 250px;

    .dtmf-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-gap: 10px;

        button {
            font-family: 'Roboto', sans-serif;
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background-color: #333;
            color: white;
            font-size: 24px;
            border: none;
            cursor: pointer;

            &:active {
                background-color: #555;
            }
        }
    }

    .keypad-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        justify-content: center;
        margin-bottom: 18px;
        color: white;

        h3 {
            margin: 0;
            font-size: 24px;
        }

        button {
            background: none;
            border: none;
            color: white;
            cursor: pointer;
            padding: 5px;
        }
    }
`;

const WebRTC = () => {
    const [activeSection, setActiveSection] = useState('Keypad');
    const [callVisible, setCallVisible] = useState(false);
    const [showDtmfKeypad, setShowDtmfKeypad] = useState(false);
    const [dtmfInput, setDtmfInput] = useState('');
    const [callInProgress, setCallInProgress] = useState(false);
    const [bnumsVisible, setBnumsVisible] = useState(false);
    const [selectedBnum, setSelectedBnum] = useState('');
    const [bnumsList, setBnumsList] = useState([]);
    const [bnumErrorStatusCode, setBnumErrorStatusCode] = useState(null);
    const [contactList, setContactList] = useState([]);
    const [encryptedSSOData, setEncryptedSSOData] = useState(null);
    const [auth, setAuth] = useState(null); // can be string (encryptedSSOData) or { userId, locationId }
    const [directCall, setDirectCall] = useState('');
    const [userAgent, setUserAgent] = useState(null);
    const [sessionState, setSessionState] = useState(null);
    const [callStatus, setCallStatus] = useState('');
    const [currentSession, setCurrentSession] = useState(null);
    const refCurrentSession = useRef(null);
    const refWatingSession = useRef(null);
    const [callInfo, setCallInfo] = useState('');
    const [callTimer, setCallTimer] = useState(0);
    const [showIncomingCall, setShowIncomingCall] = useState(false);
    const [isLoadingBnums, setIsLoadingBnums] = useState(false);
    const [configuredUserAgent, setConfiguredUserAgent] = useState(''); // done or no-bnum
    const [callWaiting, setCallWaiting] = useState(false);
    const [waitingSession, setWaitingSession] = useState(null);
    const [isCurrentCallOnHold, setIsCurrentCallOnHold] = useState(false);
    const [userAgentConnectionState, setUserAgentConnectionState] =
        useState('disconnected'); // 'connected', 'connecting', 'disconnected'

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const encryptedSSODataParam = searchParams.get('encryptedSSOData');
        const userIdParam = searchParams.get('userid');
        const locationIdParam = searchParams.get('locationid');
        const numberParam = searchParams.get('number')?.replace(/\s+/g, '');

        if (encryptedSSODataParam) {
            const decoded = decodeURIComponent(encryptedSSODataParam);
            setEncryptedSSOData(decoded);
            setAuth(decoded);
        } else if (userIdParam && locationIdParam) {
            console.log('userIdParam', userIdParam);
            console.log('locationIdParam', locationIdParam);
            setAuth({
                userId: decodeURIComponent(userIdParam),
                locationId: decodeURIComponent(locationIdParam),
            });
            console.log('auth', auth);
        }

        if (numberParam) {
            setDirectCall(numberParam);
            setCallInProgress(true);
            console.log('Direct call number:', numberParam);
        }
    }, []);

    useEffect(() => {
        setConfiguredUserAgent('');
        if (
            !selectedBnum ||
            !selectedBnum.extension ||
            !selectedBnum.password
        ) {
            // Unregister the previous connection if it exists
            if (userAgent) {
                userAgent.unregister();
                userAgent.stop();
                setUserAgent(null);
                console.log(
                    'Unregistering and stopping WebRTC UA...',
                    userAgent
                );
            }
            console.log(
                'No selected Bnum or password, WebRTC UA not configured.'
            );
            return;
        }

        // Configure JsSIP
        // const socket = new JsSIP.WebSocketInterface("wss://webrtc-au.avanser.cloud:8089/ws");
        const websocketUrl = WEBRTC_CONFIG.WEBSOCKET_BASE_URL.replace(
            '{country}',
            selectedBnum.country.toLowerCase()
        );
        const socket = new JsSIP.WebSocketInterface(websocketUrl);
        // const socket_uri = "webrtc-au.avanser.cloud"; // Replace with actual socket URI
        const socket_uri = WEBRTC_CONFIG.SIP_BASE_URL.replace(
            '{country}',
            selectedBnum.country.toLowerCase()
        );
        const username = selectedBnum.extension;
        const password = selectedBnum.password;

        // STUN/TURN Configuration
        const iceServers = WEBRTC_CONFIG.STUN_SERVERS;

        // Generate proper Contact URI dynamically
        const contactURI = `sip:${username}@${socket_uri};transport=ws`;
        const configuration = {
            sockets: [socket],
            uri: `sip:${username}@${socket_uri}`,
            password: password, // Replace with actual password
            display_name: username,
            // registrar_server: `sip:webrtc-au.avanser.cloud`,
            registrar_server: `sip:webrtc-${selectedBnum.country.toLowerCase()}.avanser.cloud`,
            contact_uri: contactURI, // Ensuring Contact URI is properly set
            trace_sip: WEBRTC_CONFIG.ENABLE_SIP_TRACE, // Enable SIP tracing for debugging
            iceServers: iceServers,
            iceTransportPolicy: 'relay', // Allow both STUN and TURN
            bundlePolicy: 'balanced', // Default WebRTC bundle policy
            sessionDescriptionHandlerFactoryOptions: {
                peerConnectionConfiguration: {
                    iceServers: iceServers,
                    iceTransportPolicy: 'relay', // Allow both STUN and TURN
                    bundlePolicy: 'balanced', // Default WebRTC bundle policy
                    rtcpMuxPolicy: 'require', // Ensure RTP and RTCP are multiplexed
                },
                iceGatheringTimeout: WEBRTC_CONFIG.ICE_GATHERING_TIMEOUT, // Set ICE timeout (milliseconds)
            },
        };

        const ua = new JsSIP.UA(configuration);
        setUserAgent(ua);

        ua.start();
        // Start of new codes
        let session = '';

        setConfiguredUserAgent('done');

        ua.on('newRTCSession', function (data) {
            session = data.session;
            // setSessionState(session);

            session.on('accepted', function () {
                console.log('Call accepted!____');
            });

            session.on('failed', function (e) {
                console.error('Call failed:', e.cause);
            });

            if (session.direction === 'incoming') {
                console.log(
                    'Incoming call from:',
                    session.remote_identity.uri.toString()
                );
                // Check if already on a call
                if (refCurrentSession.current) {
                    // We have an active call, show call waiting UI
                    console.log('Call waiting...');
                    setWaitingSession(session);
                    setCallWaiting(true);
                    // setShowIncomingCall(false);
                } else {
                    // No active call, show regular incoming call UI
                    console.log('Call incoming...');
                    setShowIncomingCall(true);
                    setCurrentSession(session);
                    refCurrentSession.current = session;
                }

                // Ensure WebRTC session uses ICE candidates correctly
                session.on('peerconnection', function () {
                    console.log('WebRTC PeerConnection established.');
                    if (session.connection) {
                        session.connection.addEventListener(
                            'addstream',
                            (event) => {
                                console.log('Receiving remote audio stream...');
                                let audioElement =
                                    document.getElementById('localAudio');
                                if (!audioElement) {
                                    // Create a new audio element if it doesn't exist
                                    audioElement =
                                        document.createElement('audio');
                                    audioElement.id = 'localAudio';
                                    document.body.appendChild(audioElement);
                                }
                                audioElement.srcObject = event.stream;
                                audioElement
                                    .play()
                                    .catch((error) =>
                                        console.error(
                                            'Error playing audio:',
                                            error
                                        )
                                    );
                                // if (audioElement) {
                                //     audioElement.srcObject = event.stream;
                                //     audioElement.play().catch(error => console.error("Error playing audio:", error));
                                // } else {
                                //     console.error("Audio element not found!");
                                // }
                            }
                        );
                    } else {
                        console.error('session.connection is still null!');
                    }
                });
            } else {
                // Outgoing call
                setCurrentSession(session);
                refCurrentSession.current = session;
                console.log('direction: ', session.direction);
                session.connection.addEventListener('addstream', (event) => {
                    console.log(event);
                    let audioElement = '';
                    audioElement = document.getElementById('localAudio');
                    audioElement.srcObject = event.stream;
                    audioElement.play();
                });
            }
        });

        // End of new codes

        // return () => {
        //     ua.stop();
        // };
        return () => {
            if (ua) {
                ua.unregister();
                ua.stop();
                setUserAgent(null);
                console.log('Unregistering and stopping WebRTC UA');

                // // Close the WebSocket connection
                // if (ua.transport && ua.transport.ws) {
                //     ua.transport.ws.close();
                //     console.log('Closing WebSocket connection...');
                // }
            }
        };
    }, [selectedBnum]);

    useEffect(() => {
        refCurrentSession.current = currentSession;
    }, [currentSession]);

    useEffect(() => {
        refWatingSession.current = waitingSession;
    }, [waitingSession]);

    // Track user agent connection state
    useEffect(() => {
        if (!userAgent) {
            setUserAgentConnectionState('disconnected');
            return;
        }

        // Set initial state
        if (userAgent.isConnected()) {
            setUserAgentConnectionState('connected');
        } else {
            setUserAgentConnectionState('connecting');
        }

        // Poll connection state every 500ms
        const checkInterval = setInterval(() => {
            const isConnected = userAgent.isConnected();
            if (isConnected) {
                setUserAgentConnectionState('connected');
            } else if (userAgentConnectionState === 'connected') {
                setUserAgentConnectionState('connecting');
            }
        }, 500);

        return () => clearInterval(checkInterval);
    }, [userAgent, userAgentConnectionState]);

    // Update your session event listeners to track hold status
    useEffect(() => {
        if (refCurrentSession.current) {
            const handleHold = () => {
                console.log('Call put on hold');
                setIsCurrentCallOnHold(true);
            };

            const handleUnhold = () => {
                console.log('Call taken off hold');
                setIsCurrentCallOnHold(false);
            };

            refCurrentSession.current.on('hold', handleHold);
            refCurrentSession.current.on('unhold', handleUnhold);

            return () => {
                if (refCurrentSession.current) {
                    refCurrentSession.current.off('hold', handleHold);
                    refCurrentSession.current.off('unhold', handleUnhold);
                }
            };
        }
    }, [refCurrentSession.current]);

    useEffect(() => {
        if (auth) {
            const fetchBnums = async () => {
                setIsLoadingBnums(true); // Set loading to true before API call
                try {
                    const data = await getBnums(auth, false);
                    setBnumErrorStatusCode(data.status);
                    setBnumsList(data.data.bnum_list);
                    if (data.data.has_selected_bnum) {
                        const myBnum = data.data.bnum_list.find(
                            (bnum) => bnum.bnum === data.data.selected_bnum
                        );
                        console.log('Selected Bnum:', myBnum);
                        if (myBnum === undefined) {
                            setCallInProgress(false);
                            setSelectedBnum('');
                            setConfiguredUserAgent('no-bnum');
                        } else {
                            if (!myBnum.country)
                                myBnum.country = myBnum.bnum.includes('+60')
                                    ? 'my'
                                    : 'au';
                            if (!myBnum.client_id)
                                myBnum.client_id = myBnum.extension.substring(
                                    0,
                                    4
                                );
                            setSelectedBnum(myBnum);
                        }
                    } else {
                        setCallInProgress(false);
                        setSelectedBnum('');
                        setConfiguredUserAgent('no-bnum');
                    }
                    console.log('Fetched Bnums:', data.data.bnum_list);
                } catch (error) {
                    setSelectedBnum('');
                    // No bnums fetched, display error message
                    setCallInProgress(false);
                    setConfiguredUserAgent('no-bnum');
                    console.error('Error fetching Bnums:', error);
                } finally {
                    setIsLoadingBnums(false); // Set loading to false after API call completes
                }
            };

            fetchBnums();
        }
    }, [auth]);

    useEffect(() => {
        let interval;
        if (callStatus === 'active') {
            interval = setInterval(() => {
                setCallTimer((prevTimer) => prevTimer + 1);
            }, 1000);
        } else {
            setCallTimer(0);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [callStatus]);

    const handleCall = (callName, callNumber) => {
        if (!userAgent) {
            console.error('User agent not initialized');
            setCallInProgress(false);
            return;
        }

        // Validate that a target number is provided
        if (!callNumber || callNumber.trim() === '') {
            console.error('Cannot initiate call: No target number provided');
            setCallInProgress(false);
            return;
        }

        // Validate phone number using E.164 validation with fallback to leading 0
        if (selectedBnum?.country) {
            const country = selectedBnum.country.toLowerCase();
            const result = validatePhoneNumberWithLeadingZeroFallback(
                callNumber,
                country
            );

            if (!result.isValid) {
                console.error(`Cannot initiate call: ${result.errorMessage}`);
                setCallInProgress(false);
                // You might want to show an error message to the user here
                return;
            }

            // Use the formatted number if leading zero was added
            if (result.formattedNumber) {
                callNumber = result.formattedNumber;
            }
        }

        // Check if user agent is connected, if not, wait for it
        if (!userAgent.isConnected()) {
            console.log('User agent not connected, waiting for connection...');

            // Create a promise that resolves when the user agent is connected
            const waitForConnection = new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (userAgent.isConnected()) {
                        clearInterval(checkInterval);
                        console.log(
                            'User agent connected, proceeding with call'
                        );
                        resolve();
                    }
                }, 500);

                // Set a timeout of 10 seconds
                setTimeout(() => {
                    clearInterval(checkInterval);
                    if (userAgent.isConnected()) {
                        console.log(
                            'User agent connected, proceeding with call'
                        );
                        resolve();
                    } else {
                        console.error(
                            'User agent failed to connect within timeout'
                        );
                        resolve(); // Resolve anyway to avoid hanging
                    }
                }, 10000);
            });

            // Wait for connection before proceeding
            waitForConnection.then(() => {
                if (userAgent.isConnected()) {
                    initiateCall(callName, callNumber);
                } else {
                    console.error(
                        'Cannot initiate call: User agent not connected'
                    );
                    setCallInProgress(false);
                }
            });
            return;
        }

        initiateCall(callName, callNumber);
    };

    const initiateCall = (callName, callNumber) => {
        // Validate that a target number is provided
        if (!callNumber || callNumber.trim() === '') {
            console.error('Cannot initiate call: No target number provided');
            return;
        }

        // Convert number to E.164 format for SIP URI
        const country = selectedBnum?.country?.toLowerCase();
        let phoneNumber;

        if (country) {
            // Convert to E.164 format
            const e164Number = toE164(callNumber, country);
            // Remove the + prefix for SIP URI
            phoneNumber = e164Number.startsWith('+')
                ? e164Number.substring(1)
                : e164Number;
        } else {
            // Fallback: just strip + if present
            phoneNumber = callNumber.startsWith('+')
                ? callNumber.substring(1)
                : callNumber;
        }

        setCallInfo({ name: callName, number: phoneNumber });

        const options = {
            mediaConstraints: { audio: true, video: false },
        };

        const sipUri = `sip:${phoneNumber}@${WEBRTC_CONFIG.SIP_BASE_URL.replace('{country}', selectedBnum.country.toLowerCase())}`;
        const session = userAgent.call(sipUri, options);
        setCurrentSession(session);
        refCurrentSession.current = session;

        session.on('connecting', () => {
            console.log('Call is connecting...');
            setCallStatus('connecting');
            setCallVisible(true);
            setCallInProgress(false);
            setDtmfInput('');
        });

        session.on('progress', () => {
            console.log('Call is in progress...');
            setCallStatus('progress');
        });

        session.on('failed', (e) => {
            console.log('Call failed', e);
            setCallStatus('');
            setCurrentSession(null);
            refCurrentSession.current = null;
            setCallVisible(false);
            setCallInProgress(false);
        });

        session.on('ended', () => {
            console.log('Call ended');
            setCallStatus('');
            setCurrentSession(null);
            refCurrentSession.current = null;
            setCallVisible(false);
            setCallInProgress(false);
        });

        session.on('accepted', () => {
            console.log('Call accepted');
            setCallStatus('active');
        });
    };

    const updateCallName = (newName) => {
        setCallInfo((prevInfo) => {
            if (prevInfo && prevInfo.number) {
                return { ...prevInfo, name: newName };
            }
            return prevInfo;
        });
    };

    const handleHangUp = () => {
        if (currentSession) {
            currentSession.terminate();
            setCallTimer(0);
        }
    };

    const handleCallAction = () => {
        if (callStatus === '') {
            handleCall();
        } else {
            handleHangUp();
        }
    };

    const rejectIncomingCall = (session) => {
        const cleanupSession = () => {
            setCurrentSession(null);
            refCurrentSession.current = null;
            setCallInfo('');
            // setSessionState(null);
            console.log('Session cleanup completed');
        };
        console.log('rejecting incoming call', session);
        if (session) {
            session.terminate();
            console.log(
                `Number ${session.remote_identity.uri.toString().split('@')[0].replace('sip:', '')} Rejected!`
            );
        }
        cleanupSession();
        setShowIncomingCall(false);
    };

    const handleRejectWaitingCall = (session) => {
        if (session === 'currentSession') {
            console.log('Current call rejected!');
            refCurrentSession.current.terminate();
            refCurrentSession.current = null;
            setCurrentSession(null);
        } else if (session && session !== 'currentSession') {
            session.terminate();
            console.log(
                `Number ${session.remote_identity.uri.toString().split('@')[0].replace('sip:', '')} Dropped!`
            );
        }
        setCallWaiting(false);
        setWaitingSession(null);
    };

    const handleRejectCurrentCall = (currentCall, waitingCall) => {
        if (refCurrentSession.current && currentCall) {
            // Drop current call
            console.log('Current call ended');
            refCurrentSession.current.terminate();
        }
        if (refWatingSession.current && refWatingSession.current.isOnHold()) {
            console.log(
                'Waiting session: ',
                refWatingSession.current.remote_identity.uri
                    .toString()
                    .split('@')[0]
                    .replace('sip:', '')
            );
            // if waiting call is on hold, answer it
            console.log('Transfering waiting call to current call');
            setCurrentSession(refWatingSession.current);
            refCurrentSession.current = refWatingSession.current;
            refWatingSession.current.unhold();
            setWaitingSession(null);
        } else {
            // if waiting call is not on hold, reject it
            console.log('No wating call to reject');
            setCurrentSession(null);
            refCurrentSession.current = null;
            setCallWaiting(false);
        }
    };

    const handleHoldCurrentAndAnswerWaiting = (currentCall, waitingCall) => {
        // Put current call on hold
        if (currentCall && currentCall.isEstablished() && waitingCall) {
            currentCall.hold();
            console.log('holded:', currentCall);
            setWaitingSession(currentCall);
        }

        if (showIncomingCall) setShowIncomingCall(false);

        // Answer the waiting call
        if (waitingCall) {
            setCurrentSession(waitingCall);
            refCurrentSession.current = waitingCall;
            if (waitingCall.isEstablished()) {
                waitingCall.unhold();
                console.log('Waiting call unhold');
            } else {
                console.log('Waiting call answered');
                waitingCall.answer({
                    mediaConstraints: { audio: true, video: false },
                });
            }
        } else {
            console.log(
                'No wating call to answer - keep talking with current call'
            );
        }

        // setWaitingSession(null);
    };

    const handleAnswerAndHoldWaiting = (waitingCall) => {
        if (waitingCall) {
            if (showIncomingCall) setShowIncomingCall(false);
            console.log('call info:', callInfo);
            waitingCall.answer({
                mediaConstraints: { audio: true, video: false },
            });

            // Give a small delay before putting the call on hold to ensure it's properly established
            setTimeout(() => {
                if (waitingCall.isEstablished()) {
                    // Put the call on hold which should mute its audio
                    waitingCall.hold();

                    // Make sure the current call's audio is active
                    if (currentSession && currentSession.isEstablished()) {
                        // Ensure current session is not on hold
                        if (currentSession.isOnHold()) {
                            currentSession.unhold();
                        }

                        // Ensure audio routing is correct by refreshing the audio element
                        if (
                            currentSession.connection &&
                            currentSession.connection.getRemoteStreams()[0]
                        ) {
                            const audioElement =
                                document.getElementById('localAudio');
                            if (audioElement) {
                                audioElement.srcObject =
                                    currentSession.connection.getRemoteStreams()[0];
                                audioElement
                                    .play()
                                    .catch((error) =>
                                        console.error(
                                            'Error playing audio:',
                                            error
                                        )
                                    );
                            }
                        }
                    }
                }
            }, 1000); // Increased delay to ensure call is fully established

            setWaitingSession(waitingCall);
            console.log('New incoming call answered and put on hold');
        }
    };

    const handleEndCurrentAndAnswerWaiting = (currentCall, waitingCall) => {
        // If current call is not established, and there is no waiting call, answer it
        if (currentCall && !currentCall.isEstablished() && !waitingCall) {
            currentCall.answer({
                mediaConstraints: { audio: true, video: false },
            });
            setCurrentSession(currentCall);
            refCurrentSession.current = currentCall;
            return;
        }

        // End current call
        if (currentCall) {
            console.log('Current call ended');
            currentCall.terminate();
        }

        // Answer the waiting call
        if (waitingCall) {
            waitingCall.answer({
                mediaConstraints: { audio: true, video: false },
            });
            setCurrentSession(waitingCall);
            refCurrentSession.current = waitingCall;
        }

        if (showIncomingCall) setShowIncomingCall(false);

        setWaitingSession(null);
    };

    // Add a function to toggle hold on the current call
    const toggleHoldCurrentCall = () => {
        if (
            refCurrentSession.current &&
            refCurrentSession.current.isEstablished()
        ) {
            const holdStatus = refCurrentSession.current.isOnHold();
            console.log('Hold status:', holdStatus);

            // Check if it's an object with local/remote properties or a boolean
            const isOnHold =
                typeof holdStatus === 'object'
                    ? holdStatus.local || holdStatus.remote
                    : holdStatus;

            if (isOnHold) {
                console.log('Unholding current call');
                refCurrentSession.current.unhold();

                // Ensure audio routing is correct after unholding
                setTimeout(() => {
                    if (
                        refCurrentSession.current &&
                        refCurrentSession.current.connection &&
                        refCurrentSession.current.connection.getRemoteStreams()[0]
                    ) {
                        const audioElement =
                            document.getElementById('localAudio');
                        if (audioElement) {
                            audioElement.srcObject =
                                refCurrentSession.current.connection.getRemoteStreams()[0];
                            audioElement
                                .play()
                                .catch((error) =>
                                    console.error('Error playing audio:', error)
                                );
                        }
                    }
                }, 500);
            } else {
                console.log('Putting current call on hold');
                refCurrentSession.current.hold();
            }
        } else {
            console.warn('No established session to hold/unhold');
        }
    };

    const handleCleanUpWatingSession = () => {
        setWaitingSession(null);
        console.log('Step1 - Waiting session cleaned up');
    };

    const getCurrentSessionHoldStatus = () => {
        if (
            !refCurrentSession.current ||
            !refCurrentSession.current.isEstablished()
        ) {
            return false;
        }

        const holdStatus = refCurrentSession.current.isOnHold();
        // Handle both boolean and object return types
        return typeof holdStatus === 'object'
            ? holdStatus.local || holdStatus.remote
            : holdStatus;
    };

    // Add DTMF feature for WebRTC component
    const sendDtmf = (digit) => {
        console.log(`Sending DTMF: ${digit}`);
        setDtmfInput(dtmfInput + digit);
        if (currentSession && currentSession.isEstablished()) {
            currentSession.sendDTMF(digit);
        }
    };

    const RenderPanel = () => {
        switch (activeSection) {
            case 'Keypad':
                return (
                    <KeyPad
                        handleCall={handleCall}
                        updateCallName={updateCallName}
                        auth={auth}
                        selectedBnum={selectedBnum}
                        directCall={directCall}
                        setDirectCall={setDirectCall}
                        configuredUserAgent={configuredUserAgent}
                        isLoadingBnums={isLoadingBnums}
                        userAgent={userAgent}
                        setCallInProgress={setCallInProgress}
                        setBnumsVisible={setBnumsVisible}
                        callStatus={callStatus}
                        setCallStatus={setCallStatus}
                    />
                );
            case 'Contacts':
                return (
                    <Contacts
                        handleCall={handleCall}
                        auth={auth}
                        setContactList={setContactList}
                        contactList={contactList}
                        selectedBnum={selectedBnum}
                    />
                );
            case 'Recents':
                return <Recents handleCall={handleCall} auth={auth} />;
            default:
                return <KeyPad handleCall={handleCall} auth={auth} />;
        }
    };

    return (
        <>
            <Global styles={GlobalStyles} />
            {callWaiting && (
                <CallWaiting
                    newSession={waitingSession}
                    currentSession={currentSession}
                    auth={auth}
                    firstCallInfo={callInfo}
                    setFirstCallInfo={setCallInfo}
                    onReject={handleRejectWaitingCall}
                    onRejectCurrentCall={handleRejectCurrentCall}
                    onHold={handleHoldCurrentAndAnswerWaiting}
                    onAnswerAndHoldWaiting={handleAnswerAndHoldWaiting}
                    onAnswer={handleEndCurrentAndAnswerWaiting}
                    setCallWaiting={setCallWaiting}
                    clearWatingSession={handleCleanUpWatingSession}
                    toggleHold={toggleHoldCurrentCall}
                    getCurrentSessionHoldStatus={getCurrentSessionHoldStatus}
                    selectedBnum={selectedBnum}
                />
            )}
            {showIncomingCall && (
                <IncomingCalls
                    session={currentSession}
                    auth={auth}
                    setCallInfo={setCallInfo}
                    rejectIncomingCall={rejectIncomingCall}
                    toggleHold={toggleHoldCurrentCall}
                    selectedBnum={selectedBnum}
                />
            )}
            {callVisible && (
                <CallDesign>
                    <span className="callName">{callInfo.name}</span>
                    <span className="callNumber">{callInfo.number}</span>
                    <SvgIcon sx={{ color: '#fafafa', fontSize: 100 }}>
                        <PersonIcon />
                    </SvgIcon>
                    <span className="callTimer">
                        {callStatus === 'active'
                            ? `${Math.floor(callTimer / 60)
                                  .toString()
                                  .padStart(
                                      2,
                                      '0'
                                  )}:${(callTimer % 60).toString().padStart(2, '0')}`
                            : callStatus}
                    </span>
                    {isCurrentCallOnHold && (
                        <span
                            style={{
                                color: '#f39c12',
                                fontSize: '16px',
                                marginTop: '10px',
                            }}
                        >
                            (On Hold)
                        </span>
                    )}
                    <audio id="localAudio" autoPlay></audio>
                    {/* {showDtmfKeypad && callStatus === "active" && ( */}
                    <DtmfKeypad
                        style={{
                            visibility:
                                showDtmfKeypad && callStatus === 'active'
                                    ? 'visible'
                                    : 'hidden',
                            opacity:
                                showDtmfKeypad && callStatus === 'active'
                                    ? 1
                                    : 0,
                            transition: 'visibility 0.2s, opacity 0.2s',
                        }}
                    >
                        <div className="keypad-header">
                            <h3 style={{ height: '1.5rem' }}>{dtmfInput}</h3>
                            {/* <button onClick={() => setShowDtmfKeypad(false)}>
                                    <Close sx={{ fontSize: 18 }} />
                                </button> */}
                        </div>
                        <div className="dtmf-grid">
                            {[
                                '1',
                                '2',
                                '3',
                                '4',
                                '5',
                                '6',
                                '7',
                                '8',
                                '9',
                                '*',
                                '0',
                                '#',
                            ].map((digit) => (
                                <button
                                    key={digit}
                                    onClick={() => sendDtmf(digit)}
                                >
                                    {digit}
                                </button>
                            ))}
                        </div>
                    </DtmfKeypad>
                    {/* )} */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '20px',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <button
                            onClick={handleCallAction}
                            className="callButton"
                            style={{
                                backgroundColor:
                                    callStatus === '' ? '#88caa3' : '#e74c3c',
                            }}
                        >
                            <SvgIcon>
                                <CallEnd
                                    sx={{ color: 'white', fontSize: 24 }}
                                />
                            </SvgIcon>
                        </button>
                        <button
                            onClick={toggleHoldCurrentCall}
                            className="callButton"
                            style={{
                                backgroundColor: isCurrentCallOnHold
                                    ? '#f39c12'
                                    : '#444',
                                opacity: callStatus === 'active' ? 1 : 0.5,
                            }}
                            disabled={callStatus !== 'active'}
                        >
                            <SvgIcon>
                                {isCurrentCallOnHold ? (
                                    <PlayArrow
                                        sx={{ color: 'white', fontSize: 24 }}
                                    />
                                ) : (
                                    <Pause
                                        sx={{ color: 'white', fontSize: 24 }}
                                    />
                                )}
                            </SvgIcon>
                        </button>
                        {showDtmfKeypad ? (
                            <p
                                style={{
                                    color: '#fff',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setShowDtmfKeypad(false)}
                            >
                                Hide Keypad
                            </p>
                        ) : (
                            <button
                                onClick={() => setShowDtmfKeypad(true)}
                                className="callButton"
                                style={{ backgroundColor: '#444' }}
                            >
                                <SvgIcon>
                                    {showDtmfKeypad ? (
                                        <Close
                                            sx={{
                                                color: 'white',
                                                fontSize: 24,
                                            }}
                                        />
                                    ) : (
                                        <Dialpad
                                            sx={{
                                                color: 'white',
                                                fontSize: 24,
                                            }}
                                        />
                                    )}
                                </SvgIcon>
                            </button>
                        )}
                    </div>
                </CallDesign>
            )}
            {callInProgress && (
                <InProgressCalling>
                    <div>
                        <img src="blue-logo.png" alt="Avanser" />
                        <span>Call in Progress...</span>
                    </div>
                </InProgressCalling>
            )}
            {bnumsVisible ? (
                <Bnums
                    setBnumsVisible={setBnumsVisible}
                    setSelectedBnum={setSelectedBnum}
                    selectedBnum={selectedBnum}
                    bnumsList={bnumsList}
                    auth={auth}
                    statusCode={bnumErrorStatusCode}
                />
            ) : (
                <>
                    <HeaderPanel>
                        <h2>Calling From</h2>
                        <div className="bnum-info">
                            {isLoadingBnums ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        width: '100%',
                                    }}
                                >
                                    <Skeleton
                                        variant="text"
                                        width={80}
                                        height={20}
                                    />
                                    <Skeleton
                                        variant="text"
                                        width={120}
                                        height={20}
                                    />
                                </div>
                            ) : selectedBnum?.bnum === undefined ? (
                                <span
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setBnumsVisible(true)}
                                >
                                    Click to select your number
                                </span>
                            ) : (
                                <>
                                    <span>{selectedBnum.bnum}</span>
                                    <span>•</span>
                                    <div
                                        className="select-bnums"
                                        onClick={() => setBnumsVisible(true)}
                                        title={selectedBnum.adName}
                                    >
                                        <span>{selectedBnum.adName}</span>
                                        <svg
                                            data-v-8dfce144=""
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth="2"
                                            stroke="currentColor"
                                            aria-hidden="true"
                                            className="h-4 w-4 text-gray-400"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M6 9l6 6 6-6"
                                            ></path>
                                        </svg>
                                    </div>
                                </>
                            )}
                        </div>
                        {/* Connection status indicator */}
                        {configuredUserAgent === 'done' && userAgent && (
                            <div className="connection-status">
                                <div
                                    className={`status-dot ${
                                        userAgentConnectionState
                                    }`}
                                ></div>
                                <span>
                                    {userAgentConnectionState === 'connected'
                                        ? 'Connected'
                                        : userAgentConnectionState ===
                                            'connecting'
                                          ? 'Connecting...'
                                          : 'Disconnected'}
                                </span>
                            </div>
                        )}
                    </HeaderPanel>
                    <>{RenderPanel()}</>
                    <FooterPanel>
                        <div
                            className={`panel-section ${activeSection === 'Contacts' ? 'active-section' : ''} `}
                            onClick={() => setActiveSection('Contacts')}
                        >
                            <div>
                                <svg
                                    data-v-528790b4=""
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                    stroke="currentColor"
                                    aria-hidden="true"
                                    className="h-6 w-6 text-gray-600"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M4 21.817C4.603 22 5.416 22 6.8 22h10.4c1.384 0 2.197 0 2.8-.183m-16 0a2.18 2.18 0 01-.362-.144 3 3 0 01-1.311-1.311C2 19.72 2 18.88 2 17.2V6.8c0-1.68 0-2.52.327-3.162a3 3 0 011.311-1.311C4.28 2 5.12 2 6.8 2h10.4c1.68 0 2.52 0 3.162.327a3 3 0 011.311 1.311C22 4.28 22 5.12 22 6.8v10.4c0 1.68 0 2.52-.327 3.162a3 3 0 01-1.311 1.311 2.18 2.18 0 01-.362.144m-16 0c0-.809.005-1.237.077-1.597a4 4 0 013.143-3.143C7.606 17 8.07 17 9 17h6c.93 0 1.394 0 1.78.077a4 4 0 013.143 3.143c.072.36.077.788.077 1.597M16 9.5a4 4 0 11-8 0 4 4 0 018 0z"
                                    ></path>
                                </svg>
                            </div>
                            <span>Contacts</span>
                        </div>
                        <div
                            className={`panel-section ${activeSection === 'Keypad' ? 'active-section' : ''} `}
                            onClick={() => setActiveSection('Keypad')}
                        >
                            <div>
                                <svg
                                    data-v-528790b4=""
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                    stroke="currentColor"
                                    aria-hidden="true"
                                    className="h-6 w-6 text-gray-600"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M8.4 3H4.6c-.56 0-.84 0-1.054.109a1 1 0 00-.437.437C3 3.76 3 4.04 3 4.6v3.8c0 .56 0 .84.109 1.054a1 1 0 00.437.437C3.76 10 4.04 10 4.6 10h3.8c.56 0 .84 0 1.054-.109a1 1 0 00.437-.437C10 9.24 10 8.96 10 8.4V4.6c0-.56 0-.84-.109-1.054a1 1 0 00-.437-.437C9.24 3 8.96 3 8.4 3zM19.4 3h-3.8c-.56 0-.84 0-1.054.109a1 1 0 00-.437.437C14 3.76 14 4.04 14 4.6v3.8c0 .56 0 .84.109 1.054a1 1 0 00.437.437C14.76 10 15.04 10 15.6 10h3.8c.56 0 .84 0 1.054-.109a1 1 0 00.437-.437C21 9.24 21 8.96 21 8.4V4.6c0-.56 0-.84-.109-1.054a1 1 0 00-.437-.437C20.24 3 19.96 3 19.4 3zM19.4 14h-3.8c-.56 0-.84 0-1.054.109a1 1 0 00-.437.437C14 14.76 14 15.04 14 15.6v3.8c0 .56 0 .84.109 1.054a1 1 0 00.437.437C14.76 21 15.04 21 15.6 21h3.8c.56 0 .84 0 1.054-.109a1 1 0 00.437-.437C21 20.24 21 19.96 21 19.4v-3.8c0-.56 0-.84-.109-1.054a1 1 0 00-.437-.437C20.24 14 19.96 14 19.4 14zM8.4 14H4.6c-.56 0-.84 0-1.054.109a1 1 0 00-.437.437C3 14.76 3 15.04 3 15.6v3.8c0 .56 0 .84.109 1.054a1 1 0 00.437.437C3.76 21 4.04 21 4.6 21h3.8c.56 0 .84 0 1.054-.109a1 1 0 00.437-.437C10 20.24 10 19.96 10 19.4v-3.8c0-.56 0-.84-.109-1.054a1 1 0 00-.437-.437C9.24 14 8.96 14 8.4 14z"
                                    ></path>
                                </svg>
                            </div>
                            <span>Keypad</span>
                        </div>
                        <div
                            className={`panel-section ${activeSection === 'Recents' ? 'active-section' : ''} `}
                            onClick={() => setActiveSection('Recents')}
                        >
                            <div>
                                <svg
                                    data-v-528790b4=""
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                    stroke="currentColor"
                                    aria-hidden="true"
                                    className="h-6 w-6 text-gray-600"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 6v6l4 2m6-2c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z"
                                    ></path>
                                </svg>
                            </div>
                            <span>Recents</span>
                        </div>
                    </FooterPanel>
                </>
            )}
        </>
    );
};

export default WebRTC;
